import React, { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'

import { useCompanyAddChargePointToCompanyMutation } from '../../../../../common/redux/api/web-api'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import useText from '../../../../../common/hooks/useText'
import { RootState } from '../../../../redux/store'

const formId = 'add-charge-point-form'

type AddChargePointDialogProps = {
    visible: boolean
    onSuccess: () => void
    onHide: () => void
}

type Inputs = {
    id: string
    wifiPassword: string
}

export const AddChargePointDialog = memo(function AddChargePointDialog({
    visible,
    onSuccess,
    onHide,
}: AddChargePointDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [addChargePoint, { isLoading }] = useCompanyAddChargePointToCompanyMutation()
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<Inputs>({
        defaultValues: {
            id: '',
            wifiPassword: '',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        if (visible) reset()
    }, [reset, visible])

    const doAddChargePoint = async (formValues: Inputs) => {
        try {
            await addChargePoint({
                companyId,
                chargePointCredentials: formValues,
            }).unwrap()

            toast.notifySuccess(text('lblChargerWasAdded'))
            onSuccess()
        } catch {
            toast.notifyError(text('msgChargerFailedToAdd'))
        }
    }

    const submitDisabled = isLoading || isSubmitting || (isSubmitted && !isValid)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnAdd')}
                type="submit"
                form={formId}
                disabled={submitDisabled}
                loading={isLoading || isSubmitting}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblAddChargerDialogTitle')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="add-charge-point-dialog"
        >
            <form id={formId} onSubmit={handleSubmit(doAddChargePoint)} className="flex flex-column gap-3" noValidate>
                <Input
                    id="id"
                    label={text('lblSerialNumberField')}
                    {...register('id', { required: text('msgAddChargePointSerialNumberRequired') })}
                    error={errors.id?.message}
                    required
                />
                <Input
                    id="wifiPassword"
                    label={text('lblPin')}
                    {...register('wifiPassword', { required: text('msgPinCodeRequired') })}
                    error={errors.wifiPassword?.message}
                    required
                />
            </form>
        </Dialog>
    )
})
